import { http } from '@/shared/api/api-client';
import { ApiError } from '@/shared/api/api-error';
import { isPermission } from '@/shared/auth/permissions';

import type {
  AuthTokenResponse,
  CurrentUser,
  CurrentUserResponse,
  LoginCredentials,
  Session,
} from '../types/auth.types';

/**
 * Turn the backend's token payload into a `Session`.
 *
 * Only the tokens are kept. The role in this payload is ignored on purpose:
 * what a user may do is decided by the backend and read from `/admin/me`.
 */
function toSession(response: AuthTokenResponse): Session {
  return {
    userId: response.poSId,
    accessToken: response.token,
    refreshToken: response.refreshToken,
  };
}

export async function login(credentials: LoginCredentials): Promise<Session> {
  const response = await http.post<AuthTokenResponse>('/Users/login', credentials, {
    // This request has no token yet; a 401 here means bad credentials, not an
    // expired session, so it must not trigger the refresh-and-retry path.
    skipAuthRefresh: true,
  });

  if (!response.isAutherized) {
    throw new ApiError({
      message: 'Your account is not authorized. Contact an administrator.',
      kind: 'http',
      status: 403,
      code: 'NOT_AUTHORIZED',
    });
  }

  return toSession(response);
}

/**
 * Exchange an expired access token for a new one.
 *
 * The backend validates the old token's signature (not its lifetime), checks
 * the account is still active, compares the stored refresh token, and rotates
 * it. Every failure is a 401. Note the PascalCase body, matching
 * `RefreshTokenRequest`.
 */
export async function refreshSession(accessToken: string, refreshToken: string): Promise<Session> {
  const response = await http.post<AuthTokenResponse>(
    '/Users/refresh-token',
    { Token: accessToken, RefreshToken: refreshToken },
    { skipAuthRefresh: true },
  );

  return toSession(response);
}

/** An account the Admin API refuses: it holds no admin permissions at all. */
const NO_ADMIN_ACCESS_USER: CurrentUser = {
  id: 0,
  userName: '',
  name: '',
  role: '',
  permissions: [],
  countryScope: { allCountries: false, countries: [] },
  mustChangePassword: false,
};

/**
 * The signed-in staff member — identity, current role, permissions and
 * country scope — from the Admin API (`GET /api/admin/me`).
 *
 * The Admin API answers 403 for an account without admin access (a customer,
 * a reseller, a role without admin permissions). That is not an error here:
 * it is a user with no permissions, which the login check and `RequireAuth`
 * already turn into "no access to the admin".
 *
 * `accessToken` is passed explicitly only during login, before the session is
 * stored; everywhere else the client attaches the stored token itself.
 * Permission strings this build does not know are dropped — they can only
 * hide UI, never grant it.
 */
export async function getCurrentUser(accessToken?: string): Promise<CurrentUser> {
  let response: CurrentUserResponse;
  try {
    response = await http.get<CurrentUserResponse>(
      '/admin/me',
      accessToken
        ? { headers: { Authorization: `Bearer ${accessToken}` }, skipAuthRefresh: true }
        : undefined,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) return NO_ADMIN_ACCESS_USER;
    throw error;
  }

  return {
    id: response.id,
    userName: response.userName,
    name: response.name,
    role: response.role,
    permissions: (response.permissions ?? []).filter(isPermission),
    countryScope: {
      allCountries: response.countryScope?.allCountries ?? false,
      countries: response.countryScope?.countries ?? [],
    },
    mustChangePassword: response.mustChangePassword ?? false,
  };
}

/**
 * Changes the signed-in staff member's password (POST /api/admin/me/password):
 * at least 10 characters with a letter and a digit. A wrong current password
 * comes back as a field error on currentPassword, never as a 401.
 */
export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await http.post('/admin/me/password', input);
}

/**
 * Changes the signed-in staff member's display name (PUT /api/admin/me/profile).
 * Returns whether it changed; an out-of-range name is a field error on name.
 */
export async function updateProfile(name: string): Promise<boolean> {
  return http.put<boolean>('/admin/me/profile', { name });
}

/**
 * Revoke the session's refresh token server-side.
 *
 * Keyed by the refresh token, so it works even when the access token has
 * already expired. Best effort: the local session is cleared regardless, so a
 * network failure here must not block signing out.
 */
export async function logout(refreshToken: string | null | undefined): Promise<void> {
  if (!refreshToken) return;

  try {
    await http.post('/Users/logout', { RefreshToken: refreshToken }, { skipAuthRefresh: true });
  } catch {
    // Ignored: the token is forgotten locally either way.
  }
}
